/**
 * HERE Routing Service
 * 
 * Service for calculating bus routes using HERE Routing API.
 * Uses backend proxy to avoid CORS issues.
 * 
 * NOTE: HERE is ONLY used for bus routing (when isBusRoute is true).
 * Regular routes continue to use OSRM.
 */

import type { FixedRouteStop } from '../../types/fixedRoutes';
import { getAtlasApiBaseUrl } from '../getAtlasApiBaseUrl';
import { atlasFetch } from '../api/atlasBackendClient';
import { getUserFriendlyErrorMessage } from '../../utils/errorMessages';

// Use backend proxy endpoint to avoid CORS issues
const API_BASE_URL = getAtlasApiBaseUrl();

// Timeout for HERE requests (in milliseconds)
const HERE_TIMEOUT = 20000; // 20 seconds

/**
 * Calculate route geometry between two stops using HERE via backend proxy
 * Returns the full coordinate sequence following roads
 * 
 * @param fromStop - Starting stop
 * @param toStop - Destination stop
 * @param isBusRoute - Whether this is a bus route (always true for HERE)
 */
export async function calculateRouteGeometry(
  fromStop: FixedRouteStop,
  toStop: FixedRouteStop,
  isBusRoute: boolean = true
): Promise<[number, number][] | null> {
  try {
    if (!fromStop.coordinates || !toStop.coordinates) {
      throw new Error('Stops must have coordinates');
    }

    const url = `${API_BASE_URL}/api/here/route`;
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), HERE_TIMEOUT);

    let response: Response;
    try {
      response = await atlasFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          from_coords: [fromStop.coordinates.lat, fromStop.coordinates.lng],
          to_coords: [toStop.coordinates.lat, toStop.coordinates.lng],
          is_bus_route: isBusRoute,
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
    } catch (fetchError) {
      clearTimeout(timeoutId);
      
      // Handle network errors gracefully
      if (fetchError instanceof Error) {
        if (fetchError.name === 'AbortError') {
          console.warn('[HERE] Request timed out, using fallback');
          return null;
        }
      }
      
      console.error('[HERE] Network error:', fetchError);
      return null;
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[HERE] Route geometry API error response:', errorText);
      
      // Handle specific error cases
      if (response.status === 503) {
        throw new Error(getUserFriendlyErrorMessage(
          'HERE service is not configured',
          { featureName: 'here_routing' }
        ));
      } else if (response.status === 404) {
        throw new Error('No route found between coordinates. The bus routing constraints may be too restrictive.');
      } else if (response.status === 500) {
        throw new Error('HERE server error. The routing service may be experiencing issues.');
      }
      
      throw new Error(getUserFriendlyErrorMessage(
        `HERE API error: ${response.status} ${response.statusText}`,
        { featureName: 'here_routing', statusCode: response.status }
      ));
    }

    const data = await response.json();

    // Backend returns geometry as [lat, lng] coordinates
    const coordinatesSequence: [number, number][] = data.geometry || [];

    return coordinatesSequence.length > 0 ? coordinatesSequence : null;
  } catch (error) {
    console.error('HERE route calculation error:', error);
    return null;
  }
}

/**
 * Calculate travel time and distance between two stops using HERE
 * Returns time in minutes and distance in meters
 * 
 * @param fromStop - Starting stop
 * @param toStop - Destination stop
 * @param isBusRoute - Whether this is a bus route (always true for HERE)
 */
export async function calculateTravelTimeAndDistance(
  fromStop: FixedRouteStop,
  toStop: FixedRouteStop,
  isBusRoute: boolean = true
): Promise<{ travelTimeMinutes: number; distanceMeters: number }> {
  console.log('[HERE] calculateTravelTimeAndDistance called', { fromStop: fromStop.name, toStop: toStop.name });
  
  try {
    if (!fromStop.coordinates || !toStop.coordinates) {
      console.error('[HERE] Missing coordinates', { fromStop, toStop });
      throw new Error('Stops must have coordinates');
    }

    const url = `${API_BASE_URL}/api/here/travel-time`;

    console.log('[HERE] Making request to backend proxy:', url);

    // Add timeout to fetch request
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), HERE_TIMEOUT);

    let response: Response;
    try {
      response = await atlasFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          from_coords: [fromStop.coordinates.lat, fromStop.coordinates.lng],
          to_coords: [toStop.coordinates.lat, toStop.coordinates.lng],
          is_bus_route: isBusRoute,
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
    } catch (fetchError) {
      clearTimeout(timeoutId);
      
      // Handle network errors (connection refused, timeout, etc.)
      if (fetchError instanceof Error) {
        if (fetchError.name === 'AbortError') {
          throw new Error('HERE request timed out. The routing service may be unavailable or slow. Please try again later.');
        }
        if (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('NetworkError')) {
          throw new Error('Unable to connect to routing service. Please check your internet connection and ensure the backend server is running.');
        }
      }
      
      throw new Error(`Network error: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`);
    }

    console.log('[HERE] Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[HERE] API error response:', errorText);
      
      // Handle specific error cases
      if (response.status === 503) {
        throw new Error(getUserFriendlyErrorMessage(
          'HERE service is not configured',
          { featureName: 'here_routing' }
        ));
      } else if (response.status === 404) {
        throw new Error('No route found between coordinates. The bus routing constraints may be too restrictive.');
      } else if (response.status === 500) {
        throw new Error('HERE server error. The routing service may be experiencing issues.');
      }
      
      throw new Error(getUserFriendlyErrorMessage(
        `HERE API error: ${response.status} ${response.statusText}`,
        { featureName: 'here_routing', statusCode: response.status }
      ));
    }

    const data = await response.json();
    console.log('[HERE] Response data:', { duration_seconds: data.duration_seconds, distance_meters: data.distance_meters });

    // Backend returns duration in seconds
    // Convert to minutes and round
    const durationSeconds = data.duration_seconds;
    if (!durationSeconds || isNaN(durationSeconds)) {
      console.error('[HERE] Invalid duration', durationSeconds);
      throw new Error('Invalid duration returned from HERE');
    }
    const durationMinutes = Math.round(durationSeconds / 60);
    
    // Get distance in meters
    const distanceMeters = data.distance_meters || 0;
    if (distanceMeters && isNaN(distanceMeters)) {
      console.warn('[HERE] Invalid distance', distanceMeters);
    }
    
    console.log('[HERE] Calculated travel time:', durationMinutes, 'minutes (', durationSeconds, 'seconds), distance:', distanceMeters, 'meters');

    return {
      travelTimeMinutes: durationMinutes,
      distanceMeters: distanceMeters || 0,
    };
  } catch (error) {
    console.error('[HERE] Travel time and distance calculation error:', error);
    throw error;
  }
}

/**
 * Calculate travel times and distances for multiple segments in a single batch request
 * Uses HERE Routing API (makes individual requests for each pair)
 * 
 * @param segments - Array of stop pairs to calculate
 * @param isBusRoute - Whether this is a bus route (always true for HERE)
 */
export async function calculateTravelTimesAndDistancesBatch(
  segments: Array<{ fromStop: FixedRouteStop; toStop: FixedRouteStop }>,
  isBusRoute: boolean = true
): Promise<Array<{ travelTimeMinutes: number; distanceMeters: number }>> {
  console.log('[HERE] calculateTravelTimesAndDistancesBatch called', { segmentCount: segments.length });
  
  try {
    if (segments.length === 0) {
      return [];
    }

    // Validate all stops have coordinates
    for (const segment of segments) {
      if (!segment.fromStop.coordinates || !segment.toStop.coordinates) {
        throw new Error('All stops must have coordinates');
      }
    }

    // Collect all unique coordinates
    const coordinateMap = new Map<string, number>(); // coord string -> index
    const coordinates: [number, number][] = [];
    const pairs: [number, number][] = [];

    segments.forEach((segment) => {
      const fromCoord = `${segment.fromStop.coordinates!.lat},${segment.fromStop.coordinates!.lng}`;
      const toCoord = `${segment.toStop.coordinates!.lat},${segment.toStop.coordinates!.lng}`;

      // Add from coordinate if not seen
      if (!coordinateMap.has(fromCoord)) {
        coordinateMap.set(fromCoord, coordinates.length);
        coordinates.push([segment.fromStop.coordinates!.lat, segment.fromStop.coordinates!.lng]);
      }

      // Add to coordinate if not seen
      if (!coordinateMap.has(toCoord)) {
        coordinateMap.set(toCoord, coordinates.length);
        coordinates.push([segment.toStop.coordinates!.lat, segment.toStop.coordinates!.lng]);
      }

      // Record pair indices
      const fromIdx = coordinateMap.get(fromCoord)!;
      const toIdx = coordinateMap.get(toCoord)!;
      pairs.push([fromIdx, toIdx]);
    });

    console.log('[HERE] Batch request:', { uniqueCoordinates: coordinates.length, pairs: pairs.length });

    const url = `${API_BASE_URL}/api/here/travel-time-batch`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), HERE_TIMEOUT * segments.length); // Allow more time for batch requests

    let response: Response;
    try {
      response = await atlasFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          coordinates: coordinates,
          pairs: pairs,
          is_bus_route: isBusRoute,
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
    } catch (fetchError) {
      clearTimeout(timeoutId);
      
      if (fetchError instanceof Error) {
        if (fetchError.name === 'AbortError') {
          throw new Error('HERE batch request timed out. The routing service may be unavailable or slow.');
        }
        if (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('NetworkError')) {
          throw new Error('Unable to connect to routing service. Please check your internet connection and ensure the backend server is running.');
        }
      }
      
      throw new Error(`Network error: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`);
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[HERE] Batch API error response:', errorText);
      
      // Handle specific error cases
      if (response.status === 503) {
        throw new Error(getUserFriendlyErrorMessage(
          'HERE service is not configured',
          { featureName: 'here_routing' }
        ));
      } else if (response.status === 404) {
        throw new Error('No route found for some coordinates. The bus routing constraints may be too restrictive.');
      } else if (response.status === 500) {
        throw new Error('HERE server error. The routing service may be experiencing issues.');
      }
      
      throw new Error(getUserFriendlyErrorMessage(
        `HERE API error: ${response.status} ${response.statusText}`,
        { featureName: 'here_routing', statusCode: response.status }
      ));
    }

    const data = await response.json();
    console.log('[HERE] Batch response received:', { resultCount: data.results?.length });

    // Convert results to minutes and return
    return data.results.map((result: { duration_seconds: number; distance_meters: number }) => ({
      travelTimeMinutes: Math.round(result.duration_seconds / 60),
      distanceMeters: result.distance_meters || 0,
    }));
  } catch (error) {
    console.error('[HERE] Batch travel time and distance calculation error:', error);
    throw error;
  }
}

/**
 * Calculate route geometry for a complete route with multiple stops
 * Returns an array of coordinate sequences for each segment
 * 
 * @param stops - Array of stops in route order
 * @param isBusRoute - Whether this is a bus route (always true for HERE)
 */
export async function calculateRouteGeometryForStops(
  stops: FixedRouteStop[],
  isBusRoute: boolean = true
): Promise<[number, number][][]> {
  const segments: [number, number][][] = [];

  for (let i = 0; i < stops.length - 1; i++) {
    const fromStop = stops[i];
    const toStop = stops[i + 1];

    try {
      const geometry = await calculateRouteGeometry(fromStop, toStop, isBusRoute);
      if (geometry && geometry.length > 0) {
        segments.push(geometry);
      } else {
        // Don't create fallback - throw error instead to prevent straight-line routes
        throw new Error(`Failed to calculate route geometry between stops ${i} and ${i + 1}`);
      }

      // Minimal rate limiting: wait 100ms between requests
      if (i < stops.length - 2) { // Don't delay after last segment
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    } catch (error) {
      console.error(`Failed to calculate route segment ${i} to ${i + 1}:`, error);
      // Don't create fallback - rethrow to prevent straight-line routes
      throw error;
    }
  }

  return segments;
}

